import { apiFormat } from '@cbgms/base/utils/datetime'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { AttributeName, IGenericVehicle, IVehicleAttribute } from '@cbgms/api/modules/vehicles'

export interface ITypeRowItem {
  value: IGenericVehicle
  sort: number
  columns: {
    [key: string]: IVehicleAttribute[] | undefined // key is the name of the attribute, value is the attr, null when it was a duplicate.
  }
}

const _scoreMatchTolerance = 10

/**
 * UseTypeFilter is a filter implementation specific for VPOM vehicle types
 * It takes into account things like the year ranges, 2012 will still match 2010-2015.
 * */
export const useTypeFilter = (data: ITypeRowItem[]): [ITypeRowItem[], (newFilter: string) => void, string] => {
  const [filter, setFilter] = useState('')
  const filterValue = filter.toLowerCase()

  const filteredData = useMemo(() => {
    if (!data) {
      return []
    }

    const scoredData = data.map(item => {
      let score = 0
      const keywords = filterValue.split(' ')
      keywords.forEach(k => {
        score =
          score +
          Object.values(item.columns)
            .filter(
              c =>
                c?.[0].Name !== AttributeName.EMDMDateFrom &&
                c?.[0].Name !== AttributeName.EMDMDateTo &&
                c?.[0].Name !== AttributeName.TecDocPlusDateFrom &&
                c?.[0].Name !== AttributeName.TecDocPlusDateTo
            )
            .reduce<number>((rowScore, colValues) => {
              if (!colValues) return rowScore
              if (k === '') return rowScore
              // match on general text
              if (colValues.find(colValue => colValue?.DisplayValue?.toLowerCase()?.includes(k))) {
                rowScore = rowScore + 10
              }
              return rowScore
            }, 0)

        // special cases for dates, check if the keyword looks like a year, ie:, 1999,  2011 etc.
        const kAsNumber = Number(k)
        if (!isNaN(kAsNumber) && kAsNumber >= 1980) {
          // find the DateFrom and DateTo values.
          const dateFromValue = moment(
            (item.columns[AttributeName.EMDMDateFrom] || item.columns[AttributeName.TecDocPlusDateFrom])?.[0]?.Value,
            apiFormat.yearMonthDay
          )
          const inFromRange = dateFromValue.isValid() && kAsNumber >= dateFromValue.year()
          const dateToValue = moment(
            (item.columns[AttributeName.EMDMDateTo] || item.columns[AttributeName.TecDocPlusDateTo])?.[0]?.Value,
            apiFormat.yearMonthDay
          )
          // when dateTo is not provided asume it's because the model is still in production
          const inToRange =
            (dateToValue.isValid() && kAsNumber <= dateToValue.year()) ||
            !(item.columns[AttributeName.EMDMDateTo] || item.columns[AttributeName.TecDocPlusDateTo])
          if (inFromRange && inToRange) {
            score = score + 10
          }
        }
      })
      return { ...item, matchScore: score }
    })

    const maxScore = Math.max(...scoredData.map(m => m.matchScore))
    const filteredData = scoredData.filter(
      item => (maxScore && item.matchScore >= Math.max(maxScore - _scoreMatchTolerance, 1)) || !maxScore
    )

    const sortedData = filteredData.sort((i1: any, i2: any) => {
      if (!i1 || !i2) return 0
      if (typeof i1 === 'string' && typeof i2 === 'string') {
        return getStringSortResult(i1, i2, filterValue)
      }

      let output = 0
      if (typeof i1 === 'object') {
        Object.keys(i1).forEach(key => {
          const v1 = i1[key]
          const v2 = i2[key]
          if (typeof v1 === 'string' && typeof v2 === 'string') {
            output = output + getStringSortResult(v1, v2, filterValue)
          }
        })
      }

      return output
    })
    return sortedData
  }, [data, filter])

  return useMemo(() => [filteredData, setFilter, filter], [filteredData, setFilter, filter])
}

const getStringSortResult = (s1: string, s2: string, filterValue: string) => {
  const item1 = s1.toLowerCase()
  const item2 = s2.toLowerCase()
  if (item1 === item2) return 0

  const item1HasPrio = item1.startsWith(filterValue)
  const item2HasPrio = item2.startsWith(filterValue)
  if (item1HasPrio && !item2HasPrio) return -1
  if (!item1HasPrio && item2HasPrio) return 1

  return 0
}
